import { Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Anchor, DEFAULT_MAX_SIZE } from "@tfg/ui"
import { AxiosError } from "axios"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { useValidateBulkUpdate, ValidateBulkUpdateResponseDto } from "~/gen/api"
import { BulkUpdateProductsModal } from "~/pages/Brand/DataTransfer/Import/components/BulkUpdateProductsModal"
import { ImportDocumentCard } from "~/pages/Brand/DataTransfer/Import/components/ImportDocumentCard"
import { PRODUCT_UPLOAD_DOCUMENTATION } from "~/pages/Brand/DataTransfer/config"
import { useAuth } from "~/store/auth-context"
import { showAxiosError } from "~/utils/showAxiosError"

export const BulkUpdateProductCard = () => {
  const { t } = useTranslation("dataTransfer")
  const [response, setResponse] =
    useState<ValidateBulkUpdateResponseDto | null>(null)
  const { isAdmin, user } = useAuth()
  const [
    isSummaryModalOpen,
    { open: openSummaryModal, close: closeSummaryModal },
  ] = useDisclosure(false)

  const { mutate: validateBulkUpdate, isPending } = useValidateBulkUpdate({
    mutation: {
      onError: (error: AxiosError) => {
        closeSummaryModal()
        showAxiosError({
          notificationTitle: t("upload.error"),
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
      },
      onSuccess: (data) => {
        setResponse(data.data)
      },
    },
  })

  if (!isAdmin || !user?.organization?.id) {
    return null
  }

  const handleUpload = async ({ file }: { file: File }) => {
    setResponse(null)
    openSummaryModal()
    validateBulkUpdate({
      data: {
        csvContent: await file.text(),
        organizationId: user.organization.id,
      },
    })
  }

  return (
    <>
      <ImportDocumentCard
        title={t("import.bulkUpdate.templateCard.title")}
        description={
          <Text>
            <Trans
              i18nKey="dataTransfer:import.bulkUpdate.templateCard.description"
              components={{
                a: (
                  <Anchor
                    size="xs"
                    td="underline"
                    href={PRODUCT_UPLOAD_DOCUMENTATION}
                    target="_blank"
                  />
                ),
              }}
            />
          </Text>
        }
        uploadButtonProps={{
          tooltip: t("import.bulkUpdate.templateCard.uploadButton.tooltip", {
            maxSize: DEFAULT_MAX_SIZE / 1024 ** 2,
          }),
          label: t("import.bulkUpdate.templateCard.uploadButton.label"),
          variant: "admin",
        }}
        notificationsProps={{
          erase: t("upload.erase"),
        }}
        areYouSureModalProps={{
          areYouSureText: t("upload.info"),
        }}
        uploadAction={handleUpload}
      />
      <BulkUpdateProductsModal
        opened={isSummaryModalOpen}
        onClose={closeSummaryModal}
        response={response}
        isPending={isPending}
      />
    </>
  )
}
